/**
 * SECURITY: Role Management Hook - HARDENED VERSION
 * 
 * This hook provides secure role checking using ONLY the user_roles table via RPC.
 * NEVER use profile.role directly - always use this hook for role checks.
 * 
 * Uses get_my_role() RPC function with SECURITY DEFINER for maximum security.
 */

import { useState, useEffect, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/auth/AuthProvider';

export type UserRole = 'admin' | 'instructor' | 'student' | null;

interface UseUserRoleReturn {
  role: UserRole;
  loading: boolean;
  error: string | null;
  isAdmin: boolean;
  isInstructor: boolean;
  isStudent: boolean;
  hasRole: (role: 'admin' | 'instructor' | 'student') => boolean;
}

export const useUserRole = (): UseUserRoleReturn => {
  const { user } = useAuth();
  const [role, setRole] = useState<UserRole>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const userId = user?.id;

  useEffect(() => {
    if (!userId) {
      setRole(null);
      setLoading(false);
      return;
    }

    let isMounted = true;
    let resolvedOnce = false;

    // Only the first lookup blanks the page (route guards wait on `loading`).
    // Later re-checks (auth events, role changes) update silently so the
    // dashboard is not unmounted and re-fetched a second time.
    const fetchRole = async () => {
      if (!resolvedOnce) setLoading(true);
      setError(null);

      try {
        const { data, error: rpcError } = await supabase.rpc('get_my_role');
        if (!isMounted) return;

        if (rpcError) {
          console.error('RPC error fetching role:', rpcError);
          try {
            const { data: viewData } = await supabase
              .from('v_my_role')
              .select('role')
              .single();
            if (!isMounted) return;
            setRole((viewData?.role as UserRole) ?? null);
          } catch (viewError) {
            console.error('Fallback role view failed:', viewError);
            setError('Failed to fetch role');
            setRole(null);
          }
        } else {
          setRole((data as UserRole) ?? null);
        }
      } catch (err) {
        if (!isMounted) return;
        console.error('Exception fetching user role:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch role');
        setRole(null);
      } finally {
        if (isMounted) {
          resolvedOnce = true;
          setLoading(false);
        }
      }
    };

    fetchRole();

    // Token refreshes and the initial-session event do not change the role;
    // re-check only when the signed-in identity is (re)established or updated.
    const { data: authListener } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN' || event === 'USER_UPDATED') fetchRole();
    });

    const channel = supabase
      .channel(`user-role-${userId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_roles',
          filter: `user_id=eq.${userId}`
        },
        () => {
          fetchRole();
        }
      )
      .subscribe();

    return () => {
      isMounted = false;
      authListener?.subscription?.unsubscribe?.();
      supabase.removeChannel(channel);
    };
  }, [userId]);

  const flags = useMemo(() => ({
    isAdmin: role === 'admin',
    isInstructor: role === 'instructor',
    isStudent: role === 'student',
  }), [role]);

  const hasRole = (checkRole: 'admin' | 'instructor' | 'student'): boolean => {
    return role === checkRole;
  };

  return {
    role,
    loading,
    error,
    ...flags,
    hasRole,
  };
};
